import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChartLine, ArrowUpRight, Clock, Gift } from "@phosphor-icons/react";

function RedeemAccessCodeDialog({ onRedeemed }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return toast.error("Enter an access code");
    setBusy(true);
    try {
      const { data } = await api.post("/access-codes/redeem", { code: trimmed });
      toast.success(`Redeemed! ${data.tools_granted} tool(s) unlocked for ${data.duration_days} days`);
      setCode("");
      setOpen(false);
      onRedeemed?.();
    } catch (err) {
      toast.error(extractError(err));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-sm font-bold" data-testid="redeem-code-open">
          <Gift size={16} weight="bold" className="mr-2" /> Redeem access code
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Redeem your access code</DialogTitle>
          <DialogDescription>
            Got a code from your instructor or admin? Enter it here to unlock free access to the assigned tools.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2 block">Access code</label>
            <Input
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="STUDENT2026"
              data-testid="redeem-code-input"
              className="rounded-sm font-mono uppercase tracking-widest"
            />
            <p className="text-xs text-muted-foreground mt-2">Your email must be on the invite list for this code.</p>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="redeem-code-submit" className="w-full bg-primary text-black hover:bg-primary/90 font-bold rounded-sm">
              {busy ? "Redeeming..." : "Redeem code"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MySubscriptions() {
  const [subs, setSubs] = useState([]);
  useEffect(() => { api.get("/subscriptions/mine").then((r) => setSubs(r.data.subscriptions || [])).catch(() => {}); }, []);
  if (subs.length === 0) return null;
  return (
    <div data-testid="my-subscriptions" className="mt-12 border border-border rounded-sm bg-card p-6">
      <div className="text-xs font-mono uppercase tracking-[0.2em] text-primary mb-2">// Auto-debit subscriptions</div>
      <div className="divide-y divide-border">
        {subs.map((s) => (
          <div key={s.subscription_id} data-testid={`my-sub-${s.subscription_id}`} className="py-3 flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="font-display font-bold">{s.tool_name}</span>
            <span className={`font-mono text-xs uppercase tracking-widest ${s.status === "active" || s.status === "authenticated" ? "text-primary" : "text-destructive"}`}>{s.status}</span>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-4">Subscriptions renew automatically through Razorpay. To stop a subscription, contact us and we will cancel it for you.</p>
    </div>
  );
}

export default function Library() {
  const [lib, setLib] = useState({ active_tools: [] });
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/library").then((r) => setLib(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="library-page" className="min-h-[calc(100vh-4rem)] px-6 md:px-8 py-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between gap-4 mb-10 pb-6 border-b border-border">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-primary mb-2">// My Library</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Your tools</h1>
            <p className="text-muted-foreground mt-2 text-sm font-mono">{lib.active_tools.length} active tools</p>
          </div>
          <div className="flex items-center gap-2">
            <RedeemAccessCodeDialog onRedeemed={load} />
            <Link to="/store"><Button data-testid="library-store-btn" className="bg-primary text-black hover:bg-primary/90 rounded-sm font-bold">Browse store <ArrowUpRight size={16} weight="bold" className="ml-1" /></Button></Link>
          </div>
        </div>

        {loading && <div className="text-muted-foreground font-mono text-sm">Loading...</div>}

        {!loading && lib.active_tools.length === 0 && (
          <div className="text-center py-20 border border-border rounded-sm">
            <p className="text-muted-foreground mb-4">No active subscriptions yet.</p>
            <div className="flex items-center justify-center gap-2">
              <RedeemAccessCodeDialog onRedeemed={load} />
              <Link to="/store"><Button data-testid="library-empty-store" className="bg-primary text-black hover:bg-primary/90 font-bold rounded-sm">Subscribe to tools</Button></Link>
            </div>
          </div>
        )}

        {lib.active_tools.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lib.active_tools.map((t) => {
              const daysLeft = t.expires_at ? Math.ceil((new Date(t.expires_at) - new Date()) / 86400000) : null;
              return (
                <Link key={t.id} to={`/tools/${t.id}`} data-testid={`lib-tool-${t.slug}`} className="group border border-border p-6 hover:border-primary/50 hover:-translate-y-1 transition-all rounded-sm bg-card block">
                  <div className="text-xs font-mono uppercase tracking-widest text-primary mb-3">{t.category}</div>
                  <h3 className="font-display font-bold text-xl mb-2">{t.name}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">{t.description}</p>
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="text-muted-foreground flex items-center gap-1"><Clock size={12} weight="bold" /> {t.is_subscription ? "Auto-renews" : daysLeft > 0 ? `${daysLeft}d left` : "expired"}</span>
                    <span className="text-primary group-hover:underline">Open →</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
        <MySubscriptions />
      </div>
    </div>
  );
}
